<template>
    <v-dialog v-model="dialog" persistent width="90%" max-width="90%" @keydown.esc="cancel" @click:outside="cancel">
        <v-card tile height="calc(90vh)">
            <v-toolbar dark dense flat>
                <v-toolbar-title class="text-body-a2 font-weight-bold white--text"> Galería </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn color="green" class="font-weight-bold mr-4" disabled elevation="0" @click.native="accept">Seleccionar</v-btn>
                <v-btn color="red" class="font-weight-bold" elevation="0" @click.native="cancel">Cerrar</v-btn>
            </v-toolbar>

            <div class="d-flex" style="width: 100%; height: calc(100% - 48px)">
                <div class="pa-4 fill-height" style="min-width: 300px; width: 300px">
                    <perfect-scrollbar>
                        <ImageCategoryList :value="selectedCategory" @change="selectCategory" />
                    </perfect-scrollbar>
                </div>

                <div class="flex-grow-1 fill-height" style="overflow: hidden">
                    <div class="dropbox">
                        <input type="file" multiple @change="leerImagen" accept="image/*" class="input-file" />
                        <span class="d-flex fill-height align-center justify-center">
                            <v-icon x-large color="black" class="mr-4">mdi-tray-full</v-icon>
                            <span class="text-body-1 font-weight-medium"> Pulsa para añadir imágenes ... o arrástralas aquí directamente. </span>
                        </span>
                    </div>

                    <div class="d-flex" style="width: 100%; height: calc(100% - 100px)">
                        <perfect-scrollbar>
                            <v-container fluid>
                                <v-row>
                                    <v-col v-for="image in filteredImages" :key="image.id" cols="3">
                                        <div class="image-container d-flex flex-column align-center justify-center fill-height">
                                            <v-progress-circular :size="50" color="primary" indeterminate v-if="image.format === 'loading'" />
                                            <v-img v-if="image.format !== 'loading'" :src="`/uploads/${image.id}/256`" contain class="pointer pa-0" @click="selectImage(image)">
                                                <!-- <span class="text-caption" style="position:absolute;top:0;left:0;background-color:rgba(0,0,0,0.5);color:white;margin:4px;padding:4px 8px;">AAA</span> -->
                                            </v-img>

                                            <span style="width: 100%" class="d-flex text-body-2 mt-1 align-center">
                                                <v-icon color="orange" class="ml-2 mr-2" v-if="image.hasMissingContent">mdi-alert</v-icon>
                                                <v-icon color="green" class="mr-2" v-else>mdi-spellcheck</v-icon>
                                                <span class="text-caption text-break flex-grow-1 pl-2 pr-2" style="max-height: 40px; overflow: hidden">{{ image.name }}</span>
                                                <v-btn icon>
                                                    <v-icon>mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </span>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </perfect-scrollbar>
                    </div>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<style scoped lang="scss">
.ps {
    height: 100%;
}

.dropbox {
    outline: 1px dashed grey; /* the dash box */
    outline-offset: -10px;
    height: 100px;
    width: 100%;
    position: relative;
    cursor: pointer;
    overflow: hidden;
}

.input-file {
    opacity: 0; /* invisible but it's there! */
    width: 100%;
    height: 100%;
    position: absolute;
    cursor: pointer;
    z-index: 10;
}

.dropbox:hover {
    background: #e1f5fe;
}

.dropbox p {
    height: 100%;
    text-align: center;
    vertical-align: middle;
    margin-top: auto;
    margin-bottom: auto;
}

.image-container {
    cursor: pointer;

    &:hover {
        background-color: lightblue;
        outline: 1px solid #81d4fa;
    }
}
</style>

<script lang="ts">
import { EventBus } from "@/components/EventBus";
import { GetAllImageCategoriesResponse, GetAllImagesResponse } from "@/services/ClientInterfaces";
import { DataClient, GalleryRequest } from "@/services/DataClient";
import { Utilities } from "@/services/Utilities";
import ImageCategoryList from "@/views/Images/Components/ImageCategoryList.vue";
import { Component, Prop, Vue } from "vue-property-decorator";

@Component({
    components: {
        ImageCategoryList,
    },
})
export default class extends Vue {
    dialog = false;

    selectedCategory = "";
    multiple = false;

    categories: GetAllImageCategoriesResponse = [];
    images: GetAllImagesResponse = [];

    get filteredImages(): GetAllImagesResponse {
        let base: GetAllImagesResponse;

        if (this.selectedCategory === "") {
            base = [...this.images];
        } else if (this.selectedCategory === "uncategorized") {
            base = this.images.filter((x) => x.format === "loading" || !x.categoryId);
        } else if (this.selectedCategory === "unused") {
            base = this.images.filter((x) => x.format === "loading" || x.referenceCount === 0);
        } else {
            base = this.images.filter((x) => x.format === "loading" || x.categoryId === this.selectedCategory);
        }

        Utilities.sortArrayByProperty(base, "-creationDate");

        return base;
    }

    mounted() {
        EventBus.$on(DataClient.GalleryService.OpenGalleryEvent, this.handleOpenEvent);
    }

    beforeDestroy() {
        EventBus.$off(DataClient.GalleryService.OpenGalleryEvent, this.handleOpenEvent);
    }

    reset() {
        this.dialog = false;
        this.images = [];
    }

    selectCategory(category: string) {
        this.selectedCategory = category;
    }

    async refresh() {
        this.images = await DataClient.Images.getAll();
    }

    async leerImagen(e: Event) {
        const files = (e.target as HTMLInputElement)?.files;
        if (files) {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e && e.target && e.target.result) {
                        const result = e.target.result as string;
                        const base64 = result.split(",")[1];
                        this.uploadImage(file.name, base64);
                    }
                };

                reader.readAsDataURL(file);
            }
        }
    }

    selectImage(image: GetAllImagesResponse[number]) {
        this.accept({
            selection: [image.id],
        });
    }

    async uploadImage(name: string, base64Data: string) {
        const categoryId = ["", "uncategorized", "unused"].includes(this.selectedCategory) ? "" : this.selectedCategory;

        const image: GetAllImagesResponse[0] = {
            id: Math.random().toString(),
            name: name,
            width: 0,
            height: 0,
            format: "loading",
            hasMissingContent: true,
            categoryId,
            creationDate: "",
        } as GetAllImagesResponse[0];

        this.images.unshift(image);

        const response = await DataClient.Images.add({
            name: {
                es: name,
            },
            categoryId,
            data: base64Data,
        });

        if (response) {
            image.id = response.id;
            image.width = response.width;
            image.height = response.height;
            image.format = response.format;
            image.hasMissingContent = response.hasMissingContent;
            image.categoryId = response.categoryId;
            image.referenceCount = response.referenceCount;
            image.creationDate = response.creationDate;
        }
    }

    async handleOpenEvent(event: GalleryRequest) {
        this.multiple = event.multiple || false;
        this.selectedCategory = event.category || "";

        await this.refresh();

        this.dialog = true;
    }

    accept(data: any) {
        DataClient.GalleryService.close(data || true);
        this.reset();
    }

    cancel() {
        DataClient.GalleryService.close();
        this.reset();
    }
}
</script>
